﻿import { validateApiResponse } from './response-validator.js';

let SiteSearchWithProducts = (function SiteSearchWithProducts() {
	
	const HOSTNAME = window.location.hostname.split('.').slice(-2)[0];
	const SFP_URI = document.querySelector('[data-sfp-uri]').getAttribute('data-sfp-uri') ?? `https://shop.${HOSTNAME}.com/`;
	const productResultsContainer = document.querySelector('[data-result-type="Products"] [data-card-container]');
	const searchBoxElems = document.querySelectorAll('input#site-search-results, input#site-search');
	const productTabElem = document.querySelector('[data-tab-group="Products"]');
	const STORE_ID_COOKIE_NAME = 'sfp_search_store_id';
	const POSTAL_COOKIE_NAME = 'sfp_search_postal_code';
	let productUpcs = [];

//#region General API and cookie helpers
	
	const getCookie = (name) => {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);

		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		}

		return null;
	}

	const setCookie = (name, value, options = {}) => {
		const {
			minutes = 1440,
			path = '/',
			sameSite = 'Lax',
			domain = '',
			secure = false
		} = options;

		const date = new Date();
		date.setTime(date.getTime() + (minutes * 60 * 1000));

		const expires = `expires=${date.toUTCString()}`;
		const cookieParts = [
			`${name}=${value}`,
			expires,
			`path=${path}`,
			`SameSite=${sameSite}`
		];

		if (domain) cookieParts.push(`domain=${domain}`);
		if (secure) cookieParts.push('Secure');

		document.cookie = cookieParts.join('; ');
	}
	
	const deleteCookie = (name) => {
		setCookie(name, '', {
			minutes: -1
		});
	}
	
	const performApiCall = (url, options = {}) => {
		return fetch(url, {
			...options,
			credentials: 'include'
		}).then(response => {
			if (!response.ok) {
				throw new Error(`HTTP error, status: ${response.status}`);
			}
			return response.json();
		});
	}

	const performApiCallWhenIdle = (url, options = {}) => {
		return new Promise((resolve, reject) => {
			const performCall = async () => {
				try {
					const response = await fetch(url, {
						...options,
						credentials: 'include'
					});

					if (!response.ok) {
						throw new Error(`HTTP error, status: ${response.status}`);
					}

					const data = await response.json();
					resolve(data);
				} catch (error) {
					reject(error);
				}
			};

			if ('requestIdleCallback' in window) {
				window.requestIdleCallback(performCall);
			} else {
				setTimeout(performCall, 50);
			}
		});
	}
//#endregion

//#region Search
	const getPostalCode = async (lazySearch = false) => {
		const POSTAL_COOKIE_ENDPOINT = `${SFP_URI}idp/v1/users/self/location`;
		const postalCodeInitial = getCookie(POSTAL_COOKIE_NAME);
		let postalCode = postalCodeInitial;

		try {
			const postalResponse = lazySearch
				? await performApiCallWhenIdle(POSTAL_COOKIE_ENDPOINT)
				: await performApiCall(POSTAL_COOKIE_ENDPOINT);

			validateApiResponse(postalResponse, 'postalCodeResponse');
			
			postalCode = postalResponse.postal_code;
			setCookie(POSTAL_COOKIE_NAME, postalCode, { minutes: 5 });
			
			if(postalCode !== postalCodeInitial) {
				deleteCookie(STORE_ID_COOKIE_NAME);
			}
		} catch (error) {
			console.error('Failed to fetch postal code:', error);
		}
		
		return postalCode;
	}

	const getStoreId = async (lazySearch = false) => {
		const STORE_ID_COOKIE_ENDPOINT = `${SFP_URI}idp/v1/shops?postal_code=`
		let storeId = getCookie(STORE_ID_COOKIE_NAME);
		if (!storeId) {
			try {
				let postalCode = await getPostalCode(lazySearch);
				const storeResponse = lazySearch ?
					await performApiCallWhenIdle(STORE_ID_COOKIE_ENDPOINT + postalCode) :
					await performApiCall(STORE_ID_COOKIE_ENDPOINT + postalCode);
				
				validateApiResponse(storeResponse, 'shopsResponse');
				
				
				storeId = storeResponse.shops[0].id;
				setCookie(STORE_ID_COOKIE_NAME, storeId);
			} catch (error) {
				console.error('Failed to fetch store ID:', error);
			}
		}
		return storeId;
	}

	const searchForProductUpcs = async (query, topN = 6) => {
		if (!query) {
			query = '';
		}
		try {
			let storeId = await getStoreId();
			let url = `${SFP_URI}idp/v1/products/search?shop_id=${storeId}&query=${encodeURIComponent(query)}`;
			
			const response = await performApiCall(url, {
				method: 'POST'
			});
			
			validateApiResponse(response, 'searchResponse');

			return response.products.map(product => product.id).slice(0, topN);
		} catch (error) {
			console.error('Product search failed:', error);
			throw error;
		}
	}

	const getProductDetails = async (productIds) => {
		let url = `${SFP_URI}idp/v1/products?`;
		let expands = ['price', 'details', 'availability'];
		let storeId = await getStoreId();

		url += `shop_id=${storeId}`;
		url += buildProductDetailsUrl(productIds, expands);
		
		try {
			let response = await performApiCall(url);
			validateApiResponse(response, 'productResponse');
			return response;
		} catch (error) {
			console.error('Failed to get product details:', error);
			throw error;
		}
	}

	const buildProductDetailsUrl = (productIds = [], expands = []) => {
		let url = '';
		for (let i = 0; i < productIds.length; i++) {
			url += `&product_ids[]=${productIds[i]}`
		}
		for (let i = 0; i < expands.length; i++) {
			url += `&expands[]=${expands[i]}`
		}
		return url;
	}

	const searchForProducts = (query) => {
		if (productUpcs.length) {
			return Promise.all(
				productUpcs.map(productIds => getProductDetails(productIds))
			);
		} else {
			return searchForProductUpcs(query)
				.then(productIds => getProductDetails(productIds));
		}
	}

	function updateCount(count) {
		const productsTabEl = document.querySelector('[data-tab-group="Products"]');
		const productsResultsEl = document.querySelector('[data-result-type="Products"]');
		const totalCountEl = document.querySelector('span[data-tab-count-all]');
		const truncatedCount = count < 6 ? count : '6+'

		if (productsTabEl) {
			const countEl = productsTabEl.querySelector('span[data-tab-count]');

			if (countEl) {
				countEl.innerHTML = `${truncatedCount}`;
				countEl.setAttribute('data-tab-count', Math.min(count, 6).toString());
			}
		}

		if (productsResultsEl) {
			const countEl = productsResultsEl.querySelector('span[data-search-group-count]');
			const footerCountEl = document.querySelector('span[data-results-footer-count]');

			if (countEl) {
				countEl.innerHTML = `(${truncatedCount})`;
				countEl.setAttribute('data-search-group-count', Math.min(count, 6).toString());
			}
			if (footerCountEl) {
				footerCountEl.innerHTML = `${Math.min(count, 6).toString()}`;
				footerCountEl.setAttribute('data-results-footer-count', Math.min(count, 6).toString());
			}
		}

		if (totalCountEl) {
			const counts = document.querySelectorAll("span[data-tab-count]");

			let sum = 0;
			counts.forEach(countEl => {
				sum += parseInt(countEl.getAttribute('data-tab-count')) || 0;
			})
			totalCountEl.innerHTML = sum;
			totalCountEl.setAttribute('data-tab-count-all', sum);
		}
	}

	function populateCards(products) {
		if (products.length === 0) {
			productResultsContainer.style.display = 'none';
		}
		productResultsContainer.innerHTML = '';

		const isMobile = productResultsContainer.getAttribute('data-card-container') === 'mobile';		
		
		products.forEach(product => {
			const quantityType = product.shop_level_attributes.quantity_attributes.quantity_type ?? 'EACH';

			const productSizeSection = product.shop_level_attributes && product.shop_level_attributes.size
				? `<div class="product-size">${product.shop_level_attributes.size}</div>`
				: ''
			
			const productDescription = formatProductDesc(product?.shop_level_attributes?.details?.details_description);
			
			let cardHtml = `
				<a class="product-card content-card" target="_blank" href="${product.permalink_url}">
					<div class="content-card-image">
						<img src="${product.image_url}" alt="${product.name}" />
					</div>
					<div class="content-card-content">
						<p class="h4 content-card-title">${product.name}</p>
						${productSizeSection}
						${productDescription}
					</div>
					<div data-addtocartbtn>
						<button class="add-to-cart__btn" data-add-cart-button data-product-id="${product.id}" data-quantity-type="${quantityType}">
							<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none"><path fill="#fff" d="M5.143 6.857H0V5.143h5.143V0h1.714v5.143H12v1.714H6.857V12H5.143V6.857Z"></path></svg>
							<span class="add-to-cart__txt">Add <span class="add-to-cart__subtxt">to Cart</span>
						</button>
					</div>
				</a>
			`
			
			if (isMobile) {
				cardHtml = `
				<div class="swiper-slide content-card">
					${cardHtml}
				</div>
				`
			}
			productResultsContainer.insertAdjacentHTML('beforeend', cardHtml);
		})
	}

	function formatProductDesc(description) {
		if(!description) {
			return ''
		}

		const firstLine = description.split('\n')[0];
		let truncated = firstLine.slice(0, 90);
	
		if(truncated !== description) {
			truncated = truncated
				.replace(/\s+\S*$/, '')  // Remove partial word at the end
				.replace(/[\s.,;:!?]+$/, '') // Remove trailing punctuation
				.concat('...');
		}
		
		return `<div class="content-card-description text-transform-none">${truncated}</div>`
	}
//#endregion
	
//#region Add to Cart
	const addProductToCart = async (productId, quantityType) => {
		let cartId = await getPersonalCartId();
		let items = await getCartItems(cartId);

		let index = items.findIndex(item => item.product_id === productId);
		if (index >= 0) {
			items[index].quantity++;
		}
		else {
			items.push({
				product_id: productId,
				quantity: 1,
				quantity_type : quantityType
			})
		}

		try {
			let url = `${SFP_URI}idp/v1/carts/${cartId}/items`;
			let body = {
				shop_id: await getStoreId(),
				cart_items: items
			}
			
			await performApiCall(url, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			updateCartWidget();
		} catch (error) {
			console.error('Failed to add product to cart:', error);
		}
	}
	
	const getPersonalCartId = async () => {
		const CART_ID_ENDPOINT = `${SFP_URI}idp/v1/carts/personal`
		try {
			const response = await performApiCall(CART_ID_ENDPOINT, {
				method: 'GET'
			});

			validateApiResponse(response, 'cartIdResponse');
			return response.carts[0].id;

		} catch (error) {
			console.error('Failed to get personal cart:', error);
		}
	}
	
	const getCartItems = async (cartId) => {
		let GET_CART_ITEMS_ENDPOINT = `${SFP_URI}idp/v1/carts/${cartId}/items`;
		try {
			const response = await performApiCall(GET_CART_ITEMS_ENDPOINT, {
				method: 'GET'
			});

			validateApiResponse(response, 'cartItemsResponse');
			return response.items;

		} catch (error) {
			console.error('Failed to get existing cart items:', error);
		}
	}
	
	const updateCartWidget = () => {
		document.dispatchEvent(new CustomEvent('ic-cart-refresh'));
	}
	
	const attachAddToCartEvents = () => {
		document.querySelectorAll('[data-add-cart-button]').forEach(button => {
			button.addEventListener('click', async (e) => {
				e.preventDefault();
				const productId = e.currentTarget.getAttribute('data-product-id');
				const quantityType = e.currentTarget.getAttribute('data-quantity-type');
				await addProductToCart(productId, quantityType);
			});
		})
	}
	
	function forceReflow(elements) {
		if (!elements) return;

		if (elements instanceof Element) {
			void elements.offsetHeight;
		} else if (NodeList.prototype.isPrototypeOf(elements) || Array.isArray(elements)) {
			elements.forEach(el => void el.offsetHeight);
		}
	}
	
	function afterNextFrameAndReflow(elements, callback) {
		requestAnimationFrame(() => {
			forceReflow(elements);
			elements.forEach(el => {
				const target = el.querySelector('[data-addtocartbtn]');
				if (target) {
					target.classList.add('add-to-cart__container');
				}
			})
			if (typeof callback === 'function') {
				callback();
			}
		});
	}
//#endregion
	function attachEvents() {
		document.addEventListener('DOMContentLoaded', async () => {
			await new Promise(resolve => setTimeout(resolve, 500));

			await getPostalCode(true);
			await getStoreId(true);
			
			if (productTabElem) {
				let query = new URLSearchParams(window.location.search).get('searchTerm');
				searchForProductUpcs(query).then(response => {
					productUpcs = response;
					updateCount(productUpcs.length);
				}).catch(error => {
					console.error('Failed to search for products:', error);
				});
			}

			if (productResultsContainer) {
				let query = new URLSearchParams(window.location.search).get('searchTerm');
				searchForProducts(query).then(response => {
					let products = response.products;
					populateCards(products);
					updateCount(products.length);
					afterNextFrameAndReflow(document.querySelectorAll('.product-card.content-card'), () => {
						attachAddToCartEvents();
					});
				}).catch(error => {
					console.error('Failed to search for products:', error);
				});
			}
			
			if (searchBoxElems) {
				searchBoxElems.forEach(searchBox => {
					searchBox.addEventListener('focus', async () => {
						await getPostalCode(true);
					});
				});
			}
		});
	}
	
	attachEvents();
}());
